import { Link } from 'react-router-dom'
import { useParams } from 'react-router-dom'
import { useChurch } from '../hooks/useChurches'
import { PublicLayout } from '../components/layout/PublicLayout'
import { useAuthStore } from '../store/auth'
import { MapPin, Eye, CalendarCheck, ArrowRight, Check } from 'lucide-react'
import { DENOMINATION_OPTIONS } from '@fellowship/shared'

function denominationLabel(key: string | null) {
  if (!key) return null
  return DENOMINATION_OPTIONS.find((d) => d.key === key)?.label ?? null
}

export default function ChurchWelcomePage() {
  const { slug } = useParams<{ slug: string }>()
  const { data: churchData, isLoading } = useChurch(slug)
  const { user } = useAuthStore()
  const church = churchData?.data

  if (isLoading) {
    return (
      <PublicLayout>
        <div className="flex items-center justify-center" style={{ height: 'calc(100vh - 80px)' }}>
          <p className="text-[13px] text-muted">Loading…</p>
        </div>
      </PublicLayout>
    )
  }

  if (!church) {
    return (
      <PublicLayout>
        <div className="flex flex-col items-center justify-center gap-4 px-6 text-center" style={{ height: 'calc(100vh - 80px)' }}>
          <p className="text-[15px] text-ink-2">We could not find that church listing.</p>
          <Link to="/for-churches" className="text-primary text-[13px] no-underline font-medium">
            Learn about Fellowship for churches →
          </Link>
        </div>
      </PublicLayout>
    )
  }

  if (church.claimed) {
    return (
      <PublicLayout>
        <div className="flex flex-col items-center justify-center gap-4 px-6 text-center" style={{ height: 'calc(100vh - 80px)' }}>
          <div className="text-[40px]">🏛️</div>
          <p className="serif text-[22px] m-0">{church.name} is already managed on Fellowship.</p>
          <p className="text-[14px] text-ink-2 m-0">This listing has already been claimed by an admin.</p>
          <Link to={`/churches/${church.slug}`} className="text-primary text-sm no-underline font-medium">
            View their profile →
          </Link>
        </div>
      </PublicLayout>
    )
  }

  const hasTraffic = church.viewCount > 0 || church.planToAttendCount > 0
  const denomLabel = denominationLabel(church.denomination)

  return (
    <PublicLayout>
      <div className="max-w-[600px] mx-auto px-6 py-14">

        {/* ── Church identity ───────────────────────────────────────────── */}
        <div className="mb-2">
          <span className="inline-block text-[10px] font-semibold uppercase tracking-[0.1em] text-warn bg-[#fff8e1] border border-warn rounded-full px-2.5 py-0.5">
            Unclaimed listing
          </span>
        </div>

        <h1 className="serif text-[42px] leading-[1.05] tracking-[-0.02em] m-0 mt-3 mb-2">
          {church.name}
        </h1>

        <div className="flex items-center gap-1.5 text-[13px] text-muted mb-6">
          <MapPin size={13} />
          {[church.city, church.state].filter(Boolean).join(', ')}
          {denomLabel && <> &middot; {denomLabel}</>}
        </div>

        <p className="text-[15px] text-ink-2 leading-[1.65] m-0 mb-8">
          We found your church online and created a listing for it. Claim it to take control — manage your own schedule, enable RSVPs, and remove the "unverified" label.
        </p>

        {/* ── Traffic signal ────────────────────────────────────────────── */}
        {hasTraffic && (
          <div className="rounded-2xl bg-primary-soft border border-primary px-5 py-5 mb-7">
            <div className="eyebrow text-primary mb-3">Your listing is already getting attention</div>
            <div className="flex gap-8">
              {church.viewCount > 0 && (
                <div className="flex items-center gap-2">
                  <Eye size={16} className="text-primary" />
                  <div>
                    <div className="serif text-[28px] leading-none text-ink">{church.viewCount}</div>
                    <div className="text-[11px] text-muted mt-0.5">page views</div>
                  </div>
                </div>
              )}
              {church.planToAttendCount > 0 && (
                <div className="flex items-center gap-2">
                  <CalendarCheck size={16} className="text-primary" />
                  <div>
                    <div className="serif text-[28px] leading-none text-ink">{church.planToAttendCount}</div>
                    <div className="text-[11px] text-muted mt-0.5">plan to attend</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── What you get ──────────────────────────────────────────────── */}
        <div className="mb-8">
          <div className="text-[12px] font-semibold uppercase tracking-[0.08em] text-muted mb-3">
            What claiming gives you
          </div>
          {[
            'Remove the "unverified" badge — events show as official',
            'Add and manage events directly, with RSVP support',
            'Import from Google Calendar in under 2 minutes',
            'See who\'s finding your events with a traffic dashboard',
          ].map((item) => (
            <div key={item} className="flex gap-2.5 mb-3">
              <Check size={14} className="text-primary shrink-0 mt-0.5" />
              <p className="text-[13px] text-ink-2 m-0 leading-[1.5]">{item}</p>
            </div>
          ))}
        </div>

        {/* ── CTA ───────────────────────────────────────────────────────── */}
        {!user ? (
          <div className="rounded-2xl bg-s2 border border-line-soft p-6">
            <div className="text-[14px] font-semibold text-ink mb-1.5">
              Create a free account to claim this page
            </div>
            <p className="text-[13px] text-ink-2 m-0 mb-4 leading-[1.5]">
              Claiming is free. No credit card until you enable RSVPs.
            </p>
            <div className="flex gap-2.5">
              <Link
                to={`/register?next=/churches/${church.id}/claim`}
                className="flex-1 flex items-center justify-center gap-1.5 h-11 rounded-xl bg-primary text-on-primary text-[13.5px] font-semibold no-underline"
              >
                Create account <ArrowRight size={14} />
              </Link>
              <Link
                to={`/login?next=/churches/${church.id}/claim`}
                className="flex-1 flex items-center justify-center h-11 rounded-xl border border-line text-ink text-[13.5px] font-medium no-underline"
              >
                Log in
              </Link>
            </div>
          </div>
        ) : (
          <Link
            to={`/churches/${church.id}/claim`}
            className="flex items-center justify-center gap-2 w-full h-12 rounded-xl bg-primary text-on-primary text-[15px] font-semibold no-underline"
          >
            Claim {church.name} <ArrowRight size={16} />
          </Link>
        )}

        <p className="text-[12px] text-muted text-center mt-3">
          Free to claim. By claiming you confirm you represent this church.
        </p>
      </div>
    </PublicLayout>
  )
}
